import React from "react";
import { useLang } from "@/i18n";

const pct = (a, b) => (b ? Math.round((a / b) * 100) : 0);

// Covers the printed index on the plano (right side) with compact zone totals + overall totals
export default function ZoneOverlay({ stats, scale = 1 }) {
  const { t } = useLang();
  const zones = [...(stats?.zones || [])].sort((a, b) => a.order - b.order);
  const k = stats?.kpis || { vrij: 0, bezet: 0, controle_nodig: 0, rood_lintje: 0, total: 0 };

  return (
    <div
      className="absolute rounded-xl bg-card border border-border shadow-sm flex flex-col overflow-hidden"
      style={{ left: "71.8%", top: "20.5%", width: "28%", height: "78.8%", fontSize: `${13 * Math.min(1, Math.max(0.68, scale))}px` }}
      data-testid="zone-overlay"
    >
      <div className="px-[5%] pt-[4%] flex-1 flex flex-col min-h-0">
        <p className="font-display font-bold text-foreground text-[0.85em] leading-none mb-[3%] truncate">{t("occupancy_zone")}</p>
        <div className="flex-1 flex flex-col justify-evenly min-h-0">
          {zones.map((z) => {
            const c = z.counts || { vrij: 0, bezet: 0, gereserveerd: 0, total: 0 };
            const p = pct(c.bezet, c.total);
            return (
              <div key={z.code} data-testid={`zone-card-${z.code}`}>
                <div className="flex items-center justify-between gap-1 leading-none">
                  <div className="flex items-center gap-1 min-w-0">
                    <span className="h-[1.25em] w-[1.25em] shrink-0 rounded grid place-items-center text-[0.62em] font-bold text-white" style={{ background: z.color }}>{z.code}</span>
                    <span className="text-[0.85em] font-semibold text-foreground/90 truncate">{z.name}</span>
                  </div>
                  <span className="text-[0.77em] text-muted-foreground shrink-0">{c.bezet}/{c.total} · {p}%</span>
                </div>
                <div className="h-[0.45em] w-full rounded-full bg-slate-100 overflow-hidden mt-[3px]">
                  <div className="h-full rounded-full" style={{ width: `${p}%`, background: z.color }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-secondary border-t border-sky-200 px-[5%] py-[4%] space-y-[3px]" data-testid="totals-block">
        <p className="font-display font-bold text-foreground text-[0.77em] leading-none mb-[3%]">{t("totals")}</p>
        <div className="flex justify-between text-[0.7em] leading-tight"><span className="font-semibold text-emerald-700">{t("free")}</span><span className="tabular-nums text-foreground" data-testid="total-vrij">{k.vrij}/{k.total} · {pct(k.vrij, k.total)}%</span></div>
        <div className="flex justify-between text-[0.7em] leading-tight"><span className="font-semibold text-sky-700">{t("available_new")}</span><span className="tabular-nums text-foreground" data-testid="total-beschikbaar">{k.beschikbaar ?? k.vrij}/{k.total} · {pct(k.beschikbaar ?? k.vrij, k.total)}%</span></div>
        <div className="flex justify-between text-[0.7em] leading-tight"><span className="font-semibold text-rose-600">{t("occupied")}</span><span className="tabular-nums text-foreground" data-testid="total-bezet">{k.bezet}/{k.total} · {pct(k.bezet, k.total)}%</span></div>
        <div className="flex justify-between text-[0.7em] leading-tight"><span className="font-semibold text-violet-600">{t("kpi_controle")}</span><span className="tabular-nums text-foreground" data-testid="total-controle">{k.controle_nodig}</span></div>
        <div className="flex justify-between text-[0.7em] leading-tight"><span className="font-semibold text-rose-700">{t("red_ribbons")}</span><span className="tabular-nums text-foreground" data-testid="total-rood">{k.rood_lintje}</span></div>
        <div className="flex justify-between text-[0.62em] leading-tight text-muted-foreground pt-[2px]"><span>{t("arriving_today")} {stats?.arriving_today ?? 0}</span><span>{t("departing_today")} {stats?.departing_today ?? 0}</span></div>
      </div>
    </div>
  );
}
